import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import {
  FaUsers,
  FaBuilding,
  FaCar,
  FaEnvelope,
  FaTools,
  FaUserFriends,
  FaImage,
  FaEdit,
  FaStar,
  FaPhoneAlt
} from 'react-icons/fa';
import { MdDashboard } from 'react-icons/md';
import '../home_premium.css';

function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: 0,
    usersBreakdown: { students: 0, listers: 0, owners: 0, admins: 0 },
    messages: 0,
    messagesBreakdown: { conversations: 0, totalMessages: 0 },
    properties: 0,
    propertiesBreakdown: { listed: 0, rooms: 0, wholeProperties: 0, requests: 0, inspections: 0 },
    cars: 0,
    carsBreakdown: { rentalCars: 0, sellingCars: 0, bookings: 0, driverVerifications: 0 },
    hiremates: 0,
    hirematesBreakdown: { profiles: 0, jobs: 0, tasks: 0 },
    roommates: 0,
    banners: 0,
    bannersBreakdown: { active: 0, inactive: 0 },
    blogs: 0,
    blogsBreakdown: { published: 0, draft: 0 },
    testimonials: 0,
    testimonialsBreakdown: { approved: 0, pending: 0 },
    contactEnquiries: 0,
    contactEnquiriesBreakdown: { pending: 0, resolved: 0 }
  });

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    const safeGet = (url) => axiosInstance.get(url).then((r) => r.data).catch(() => []);

    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get('/admin/stats');
        const data = response.data || {};

        // If backend already returned the full breakdown structure, use it directly
        if (data.carsBreakdown && data.hirematesBreakdown) {
          setStats((prev) => ({
            ...prev,
            ...data
          }));
          return;
        }

        // Fallback: compute live breakdowns from existing admin endpoints if backend hasn't restarted yet
        const [
          usersList,
          convsList,
          propsList,
          propReqsList,
          inspectionsList,
          carsList,
          carBookingsList,
          driverVerifsList,
          hirematesList,
          hiremateJobsList,
          hiremateTasksList,
          flatmatesList,
          bannersList,
          blogsList,
          testimonialsList,
          contactList
        ] = await Promise.all([
          safeGet('/admin/users'),
          safeGet('/messages/conversations'),
          safeGet('/properties'),
          safeGet('/admin/property-requests'),
          safeGet('/admin/inspections'),
          safeGet('/cars/admin/all'),
          safeGet('/admin/car-bookings'),
          safeGet('/driver-verification/admin/all'),
          safeGet('/admin/hiremates'),
          safeGet('/admin/hiremate-jobs'),
          safeGet('/admin/hiremate-tasks'),
          safeGet('/flatmates/admin/all'),
          safeGet('/banners/admin/all'),
          safeGet('/blogs'),
          safeGet('/testimonials'),
          safeGet('/contact-enquiries')
        ]);

        const uArr = Array.isArray(usersList) ? usersList : [];
        const cArr = Array.isArray(carsList) ? carsList : [];
        const pArr = Array.isArray(propsList) ? propsList : [];
        const prArr = Array.isArray(propReqsList) ? propReqsList : [];
        const inspArr = Array.isArray(inspectionsList) ? inspectionsList : [];
        const cbArr = Array.isArray(carBookingsList) ? carBookingsList : [];
        const dvArr = Array.isArray(driverVerifsList) ? driverVerifsList : [];
        const hmArr = Array.isArray(hirematesList) ? hirematesList : [];
        const hjArr = Array.isArray(hiremateJobsList) ? hiremateJobsList : [];
        const htArr = Array.isArray(hiremateTasksList) ? hiremateTasksList : [];
        const fmArr = Array.isArray(flatmatesList) ? flatmatesList : [];
        const bnArr = Array.isArray(bannersList) ? bannersList : [];
        const blArr = Array.isArray(blogsList) ? blogsList : [];
        const tmArr = Array.isArray(testimonialsList) ? testimonialsList : [];
        const ceArr = Array.isArray(contactList) ? contactList : [];
        const cvArr = Array.isArray(convsList) ? convsList : [];

        const sellingCars = cArr.filter(
          (c) => String(c.listing_type || '').toLowerCase() === 'sale' || String(c.listing_type || '').toLowerCase() === 'selling'
        ).length;
        const rentalCars = Math.max(0, cArr.length - sellingCars);

        const students = uArr.filter((u) => u.role === 'student').length;
        const listers = uArr.filter((u) => u.role === 'agent').length;
        const owners = uArr.filter((u) => u.role === 'owner').length;
        const admins = uArr.filter((u) => u.role === 'admin').length;

        const totalPropRequests = prArr.reduce((acc, item) => acc + (Number(item.total_requests) || 1), 0);
        const activeBanners = bnArr.filter((b) => b.is_active !== false).length;
        const approvedTestimonials = tmArr.filter((t) => (t.status || 'approved') === 'approved').length;
        const pendingEnquiries = ceArr.filter((e) => (e.status || 'pending') === 'pending').length;

        setStats({
          users: uArr.length || data.users || 0,
          usersBreakdown: { students, listers, owners, admins },
          messages: cvArr.length,
          messagesBreakdown: { conversations: cvArr.length, totalMessages: cvArr.length },
          properties: pArr.length || data.properties || 0,
          propertiesBreakdown: {
            listed: pArr.length || data.properties || 0,
            requests: totalPropRequests,
            inspections: inspArr.length
          },
          cars: cArr.length || data.cars || 0,
          carsBreakdown: {
            rentalCars,
            sellingCars,
            bookings: cbArr.length,
            driverVerifications: dvArr.length
          },
          hiremates: hmArr.length + hjArr.length + htArr.length,
          hirematesBreakdown: {
            profiles: hmArr.length,
            jobs: hjArr.length,
            tasks: htArr.length
          },
          roommates: fmArr.length,
          banners: bnArr.length,
          bannersBreakdown: {
            active: activeBanners,
            inactive: Math.max(0, bnArr.length - activeBanners)
          },
          blogs: blArr.length,
          blogsBreakdown: {
            published: blArr.length,
            draft: 0
          },
          testimonials: tmArr.length,
          testimonialsBreakdown: {
            approved: approvedTestimonials,
            pending: Math.max(0, tmArr.length - approvedTestimonials)
          },
          contactEnquiries: ceArr.length,
          contactEnquiriesBreakdown: {
            pending: pendingEnquiries,
            resolved: Math.max(0, ceArr.length - pendingEnquiries)
          }
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  const handleSubLink = (e, path) => {
    e.stopPropagation();
    navigate(path);
  };

  const dashboardCards = [
    {
      key: 'users',
      title: 'Total Users',
      count: stats.users || 0,
      icon: <FaUsers size={26} />,
      path: '/users',
      barWidth: '80%',
      items: [
        { label: 'Students', count: stats.usersBreakdown?.students ?? 0, path: '/users' },
        { label: 'Listers / Agents', count: stats.usersBreakdown?.listers ?? 0, path: '/users' },
        { label: 'Business Owners', count: stats.usersBreakdown?.owners ?? 0, path: '/users' }
      ]
    },
    {
      key: 'properties',
      title: 'Property Management',
      count: stats.properties || 0,
      icon: <FaBuilding size={25} />,
      path: '/properties',
      barWidth: '72%',
      items: [
        { label: 'Property List', count: stats.propertiesBreakdown?.listed ?? stats.properties ?? 0, path: '/properties' },
        { label: 'Property Requests', count: stats.propertiesBreakdown?.requests ?? 0, path: '/property-requests' },
        { label: 'Inspections Booked', count: stats.propertiesBreakdown?.inspections ?? 0, path: '/inspections' }
      ]
    },
    {
      key: 'cars',
      title: 'Car Management',
      count: stats.cars || 0,
      icon: <FaCar size={26} />,
      path: '/cars',
      barWidth: '68%',
      items: [
        { label: 'Rental Cars', count: stats.carsBreakdown?.rentalCars ?? 0, path: '/cars' },
        { label: 'Selling Cars', count: stats.carsBreakdown?.sellingCars ?? 0, path: '/cars' },
        { label: 'Car Bookings / Inspections', count: stats.carsBreakdown?.bookings ?? 0, path: '/car-bookings' },
        { label: 'Driver Verifications', count: stats.carsBreakdown?.driverVerifications ?? 0, path: '/driver-verifications' }
      ]
    },
    {
      key: 'hiremates',
      title: 'Hiremate Management',
      count: stats.hiremates || 0,
      icon: <FaTools size={24} />,
      path: '/hiremates',
      barWidth: '75%',
      items: [
        { label: 'Hiremate Profiles', count: stats.hirematesBreakdown?.profiles ?? 0, path: '/hiremates' },
        { label: 'Job List', count: stats.hirematesBreakdown?.jobs ?? 0, path: '/hiremate-jobs' },
        { label: 'Task List', count: stats.hirematesBreakdown?.tasks ?? 0, path: '/hiremate-tasks' }
      ]
    },
    {
      key: 'messages',
      title: 'Messages',
      count: stats.messages || 0,
      icon: <FaEnvelope size={24} />,
      path: '/messages',
      barWidth: '65%',
      items: [
        { label: 'Active Conversations', count: stats.messagesBreakdown?.conversations ?? stats.messages ?? 0, path: '/messages' },
        { label: 'Total Chat Messages', count: stats.messagesBreakdown?.totalMessages ?? 0, path: '/messages' }
      ]
    },
    {
      key: 'roommates',
      title: 'Roommates',
      count: stats.roommates || 0,
      icon: <FaUserFriends size={26} />,
      path: '/flatmates',
      barWidth: '60%',
      items: [
        { label: 'Roommate Profiles', count: stats.roommates ?? 0, path: '/flatmates' }
      ]
    },
    {
      key: 'banners',
      title: 'Banners',
      count: stats.banners || 0,
      icon: <FaImage size={24} />,
      path: '/banners',
      barWidth: '58%',
      items: [
        { label: 'Active Banners', count: stats.bannersBreakdown?.active ?? stats.banners ?? 0, path: '/banners' },
        { label: 'Inactive Banners', count: stats.bannersBreakdown?.inactive ?? 0, path: '/banners' }
      ]
    },
    {
      key: 'blogs',
      title: 'Blogs',
      count: stats.blogs || 0,
      icon: <FaEdit size={24} />,
      path: '/blogs',
      barWidth: '62%',
      items: [
        { label: 'Published Blogs', count: stats.blogsBreakdown?.published ?? stats.blogs ?? 0, path: '/blogs' },
        { label: 'Draft Blogs', count: stats.blogsBreakdown?.draft ?? 0, path: '/blogs' }
      ]
    },
    {
      key: 'testimonials',
      title: 'Testimonials',
      count: stats.testimonials || 0,
      icon: <FaStar size={25} />,
      path: '/testimonials',
      barWidth: '70%',
      items: [
        { label: 'Approved Testimonials', count: stats.testimonialsBreakdown?.approved ?? stats.testimonials ?? 0, path: '/testimonials' },
        { label: 'Pending Approval', count: stats.testimonialsBreakdown?.pending ?? 0, path: '/testimonials' }
      ]
    },
    {
      key: 'contactEnquiries',
      title: 'Contact Us Enquiries',
      count: stats.contactEnquiries || 0,
      icon: <FaPhoneAlt size={23} />,
      path: '/contact-enquiries',
      barWidth: '64%',
      items: [
        { label: 'Pending Enquiries', count: stats.contactEnquiriesBreakdown?.pending ?? 0, path: '/contact-enquiries' },
        { label: 'Contacted / Resolved', count: stats.contactEnquiriesBreakdown?.resolved ?? 0, path: '/contact-enquiries' }
      ]
    }
  ];

  return (
    <div className="home-page-wrapper">
      {/* Animated Background Particles */}
      <div className="home-bg-anim">
        <span></span><span></span><span></span>
        <span></span><span></span><span></span>
      </div>

      {/* Premium Rounded Hero Header */}
      <div className="home-content-area">
        <div className="dash-hero-card">
          <div className="dh-blob dh-blob-1"></div>
          <div className="dh-blob dh-blob-2"></div>
          <div className="dh-blob dh-blob-3"></div>

          <div className="dh-inner">
            <div className="dh-left">
              <div className="dh-icon-box">
                <MdDashboard size={36} />
              </div>
              <div>
                <h1 className="dh-title">Dashboard Overview</h1>
                <p className="dh-subtitle">Welcome back, Admin &mdash; Here&apos;s your live summary</p>
              </div>
            </div>
            <div className="dh-right">
              <div className="dh-datetime">
                <span className="dh-time">{timeStr}</span>
                <span className="dh-date">{dateStr}</span>
              </div>
            </div>
          </div>
        </div>

        {/* All Sidebar Menu Stat Cards */}
        <div className="row g-4 mt-2">
          {dashboardCards.map((card) => (
            <div key={card.key} className="col-xl-4 col-md-6 mb-4">
              <div
                className="stat-card-v2 stat-card-v2-users h-100"
                onClick={() => navigate(card.path)}
              >
                <div>
                  <div className="scv2-glow"></div>
                  <div className="scv2-top">
                    <div className="scv2-icon-wrap">{card.icon}</div>
                    <span className="scv2-badge">Live</span>
                  </div>
                  <div className="scv2-number">{card.count}</div>
                  <div className="scv2-label">{card.title}</div>
                </div>

                <div>
                  {card.items && card.items.length > 0 && (
                    <div className="scv2-sublist">
                      {card.items.map((sub, idx) => (
                        <div
                          key={idx}
                          className="scv2-subitem"
                          onClick={(e) => handleSubLink(e, sub.path)}
                        >
                          <span className="scv2-sublabel">
                            <span className="scv2-subdot"></span>
                            {sub.label}
                          </span>
                          <span className="scv2-subcount">{sub.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="scv2-bar">
                    <div className="scv2-bar-fill" style={{ width: card.barWidth }}></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Home;

